"use client";

import React from "react";
import { PhoneCall, Clock, ShieldCheck, MapPin, ArrowRight, CalendarCheck, Zap } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function EmergencyCta() {
  const shouldReduceMotion = useReducedMotion();
  const trustHighlights = [{
    icon: Clock,
    title: "24/7 Rapid Response",
    desc: "Live dispatch ready day or night in Ellenwood and surrounding areas."
  }, {
    icon: Zap,
    title: "Same-Day Priority",
    desc: "Burst pipes, major leaks, and sewer backups diagnosed on arrival."
  }, {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    desc: "Straightforward emergency pricing with no hidden overtime surcharges."
  }];
  return <section id="emergencyclosingcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="border-border bg-card shadow-xl overflow-hidden relative">
            {/* Urgent Accent Strip */}
            <div className="h-2 w-full bg-accent" />
            
            <CardContent className="p-8 sm:p-12 lg:p-16 text-center">
              <div className="flex justify-center mb-6">
                <Badge variant="destructive" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5 shadow-xs">
                  <span className="size-2 rounded-full bg-white animate-pulse" />
                  24/7 Emergency Dispatch
                </Badge>
              </div>

              <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-card-foreground mb-4 max-w-2xl mx-auto">
                Plumbing Emergency in Ellenwood? Call Now
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                Water damage gets worse every minute. Our licensed emergency plumbers are on call across Ellenwood and Clayton County to stop active leaks, clear dangerous backups, and restore your water fast.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
                <a href="tel:4708479076" className="w-full sm:w-auto inline-flex" aria-label="Call (470) 847-9076 for emergency plumbing">
                  <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-base sm:text-lg px-8 py-6 rounded-md shadow-md gap-3 cursor-pointer">
                    <PhoneCall className="size-5" />
                    Call (470) 847-9076
                  </Button>
                </a>

                <Link to="/contact/" className="w-full sm:w-auto inline-flex">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-muted font-medium text-base px-6 py-6 rounded-md shadow-xs gap-2">
                    <CalendarCheck className="size-5 text-muted-foreground" />
                    Request Service Online
                  </Button>
                </Link>
              </div>

              {/* Reassurance Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 border-t border-border text-left">
                {trustHighlights.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex items-start gap-3.5 bg-background/60 p-4 rounded-lg border border-border/60">
                      <div className="p-2 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                        <Icon className="size-5" />
                      </div>
                      <div>
                        <h3 className="font-default font-semibold text-sm sm:text-base text-card-foreground">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 leading-snug">
                          {item.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              {/* Back to Service Areas Hub Link */}
              <div className="mt-10 pt-6 border-t border-border/50 flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground">
                <MapPin className="size-4 text-muted-foreground" />
                <span>Need service outside Ellenwood?</span>
                <Link to="/service-areas/" className="inline-flex items-center gap-1 font-semibold text-foreground hover:text-primary transition-colors underline underline-offset-4">
                  View all Service Areas
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}